import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageTitle } from '../../hooks/usePageTitle';
import { Button } from '../../components/ui/button';
import { MapPin, Anchor, Sun, Waves, ChevronRight, Ship } from 'lucide-react';
import axios from 'axios';
import { jsonLd } from '../../lib/jsonLd';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const destinationData = {
  phuket: {
    name: 'Phuket & Andaman Sea',
    country: 'Thailand',
    tagline: 'Gateway to Thailand\'s Island Paradise',
    description: 'Phuket serves as the premier yachting hub of Southeast Asia, offering world-class marinas, stunning limestone karsts of Phang Nga Bay, and easy access to the Phi Phi Islands, Similan Islands, and beyond.',
    image: 'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=1600&q=85',
    highlights: [
      { icon: Sun, label: 'Best Season', value: 'November - April' },
      { icon: Waves, label: 'Water Temp', value: '28-30°C (82-86°F)' },
      { icon: Anchor, label: 'Major Marinas', value: 'Yacht Haven, Ao Po Grand' },
      { icon: Ship, label: 'Fleet Size', value: '200+ Charter Yachts' },
    ],
    attractions: [
      'Phang Nga Bay - Iconic limestone karsts and James Bond Island',
      'Phi Phi Islands - Crystal-clear waters and vibrant nightlife',
      'Similan Islands - World-class diving (Nov-May only)',
      'Racha Islands - Pristine beaches just 2 hours from Phuket',
      'Koh Yao Noi - Authentic Thai island life',
    ],
    faqs: [
      {
        question: 'What is the best time to charter a yacht in Phuket?',
        answer: 'The best time is during the dry season from November to April. The southwest monsoon (May-October) brings rough seas to the Andaman coast, though protected areas like Phang Nga Bay remain accessible.'
      },
      {
        question: 'How much does a yacht charter cost in Phuket?',
        answer: 'Day charters start from $2,000-5,000 for smaller motor yachts. Weekly charters range from $25,000 for mid-range catamarans to $150,000+ for luxury superyachts. This typically excludes APA (provisions/fuel).'
      },
      {
        question: 'Do I need a license to charter a yacht in Thailand?',
        answer: 'No license is required for crewed charters. For bareboat charters, you\'ll need sailing certifications (ICC or equivalent) and relevant experience. Most visitors opt for crewed charters.'
      },
    ],
    locationFilter: 'Thailand',
  },
  langkawi: {
    name: 'Langkawi',
    country: 'Malaysia',
    tagline: 'Duty-Free Paradise with Calm Waters',
    description: 'Langkawi, Malaysia\'s duty-free island, offers protected waters perfect for year-round cruising, world-class marinas, and easy access to Thailand\'s southern islands. A favorite for yacht owners seeking tax advantages.',
    image: 'https://images.unsplash.com/photo-1540202404-a2f29016b523?w=1600&q=85',
    highlights: [
      { icon: Sun, label: 'Best Season', value: 'Year-round (Dec-Mar ideal)' },
      { icon: Waves, label: 'Water Temp', value: '27-30°C (81-86°F)' },
      { icon: Anchor, label: 'Major Marinas', value: 'Royal Langkawi, Telaga' },
      { icon: Ship, label: 'Duty-Free', value: 'Tax-free yacht haven' },
    ],
    attractions: [
      'Kilim Geoforest Park - Mangroves and eagle feeding',
      'Payar Island Marine Park - Excellent snorkeling',
      'Dayang Bunting Lake - Freshwater lake on an island',
      'Tarutao National Park (Thailand) - Just across the border',
      'Rebak Island - Private island resort anchorage',
    ],
    faqs: [
      {
        question: 'Why is Langkawi popular for yacht ownership?',
        answer: 'Langkawi is a duty-free zone, meaning yacht owners can import vessels without paying Malaysia\'s standard import duties. Combined with lower berthing costs than Singapore, it\'s a favorite for long-term yacht storage.'
      },
      {
        question: 'Can I cruise from Langkawi to Thailand?',
        answer: 'Yes! Langkawi is just 30 nautical miles from Thailand\'s Tarutao National Park. Many itineraries combine both countries. You\'ll need to clear customs at each border crossing.'
      },
      {
        question: 'What is the weather like in Langkawi?',
        answer: 'Langkawi has a tropical climate with two monsoon seasons. The best weather is December-March (dry season). June-September sees more rain but calmer seas in the protected Andaman waters.'
      },
    ],
    locationFilter: 'Malaysia',
  },
  'raja-ampat': {
    name: 'Raja Ampat',
    country: 'Indonesia',
    tagline: 'The Last Paradise on Earth',
    description: 'Raja Ampat in West Papua is the crown jewel of Indonesian yachting - home to the world\'s most biodiverse marine ecosystem with over 1,500 fish species and 75% of all known coral species.',
    image: 'https://images.unsplash.com/photo-1516690561799-46d8f74f9abf?w=1600&q=85',
    highlights: [
      { icon: Sun, label: 'Best Season', value: 'October - April' },
      { icon: Waves, label: 'Water Temp', value: '28-30°C (82-86°F)' },
      { icon: Anchor, label: 'Access Point', value: 'Sorong (SOQ airport)' },
      { icon: Ship, label: 'Experience', value: 'Remote expedition cruising' },
    ],
    attractions: [
      'Misool - World\'s best diving and hidden lagoons',
      'Wayag - Iconic karst islands viewpoint',
      'Pianemo - Stunning island panoramas',
      'Manta Sandy - Guaranteed manta ray encounters',
      'Cape Kri - World record fish biodiversity',
    ],
    faqs: [
      {
        question: 'How do I get to Raja Ampat by yacht?',
        answer: 'Most yacht expeditions depart from Sorong (accessible via Jakarta or Makassar). The journey from Bali takes 4-5 days sailing. Many choose liveaboard dive yachts or expedition vessels for the remote waters.'
      },
      {
        question: 'What permits are needed for Raja Ampat?',
        answer: 'All visitors need a Raja Ampat Marine Park entry permit (around $100 for foreigners). Yachts require a CAIT (Cruising Permit) for Indonesian waters, typically arranged through agents.'
      },
      {
        question: 'Is Raja Ampat suitable for beginners?',
        answer: 'Raja Ampat is best suited for experienced yacht travelers due to its remote location, limited infrastructure, and challenging navigation. For beginners, we recommend starting with Phuket or Langkawi.'
      },
    ],
    locationFilter: 'Indonesia',
  },
};

export default function DestinationPage() {
  const { slug } = useParams();
  const destination = destinationData[slug];
  const [yachts, setYachts] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState(0);

  usePageTitle(destination ? `${destination.name} Yacht Charters` : 'Destination');

  useEffect(() => {
    if (destination) {
      fetchYachts();
    }
  }, [slug]);

  const fetchYachts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/yachts?location=${destination.locationFilter}`);
      setYachts(response.data.slice(0, 3));
    } catch (error) {
      console.error('Error fetching yachts:', error);
    }
  };

  if (!destination) {
    return (
      <div className="min-h-screen pt-32 px-6 text-center">
        <h1 className="text-4xl font-medium mb-4">Destination Not Found</h1>
        <Link to="/listings">
          <Button className="bg-gold text-[#0A192F]">Browse All Yachts</Button>
        </Link>
      </div>
    );
  }

  // Schema.org JSON-LD for destination page
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    "name": destination.name,
    "description": destination.description,
    "image": destination.image,
    "containedInPlace": {
      "@type": "Country",
      "name": destination.country
    },
    "touristType": ["Yacht Charter", "Luxury Travel", "Sailing"],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": destination.faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen">
      {/* Schema.org JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(schemaData) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd(faqSchema) }} />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-end" data-testid="destination-hero">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${destination.image}')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F] via-[#0A192F]/50 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 pb-16 w-full">
          <p className="text-xs uppercase tracking-[0.2em] text-gold mb-4">{destination.country}</p>
          <h1 
            className="text-5xl md:text-7xl font-medium tracking-tight mb-4"
            style={{ fontFamily: 'Playfair Display, serif' }}
            data-testid="destination-name"
          >
            {destination.name}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl">{destination.tagline}</p>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {destination.highlights.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={idx} className="glass-light p-6 text-center">
                <Icon size={32} className="text-gold mx-auto mb-3" />
                <p className="text-white/60 text-sm mb-1">{item.label}</p>
                <p className="text-white font-semibold">{item.value}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Description & Attractions */}
      <section className="py-16 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-3xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              About {destination.name}
            </h2>
            <p className="text-white/70 leading-relaxed text-lg mb-8">{destination.description}</p>
            <Link to="/yacht-finder">
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
                Find Your Yacht <ChevronRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
          <div>
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Top Attractions
            </h3>
            <ul className="space-y-4">
              {destination.attractions.map((attraction, idx) => (
                <li key={idx} className="flex gap-3 items-start">
                  <MapPin size={18} className="text-gold mt-1 shrink-0" />
                  <span className="text-white/80">{attraction}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Available Yachts */}
      {yachts.length > 0 && (
        <section className="py-16 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }}>
              Yachts in {destination.name}
            </h2>
            <Link to={`/listings?location=${destination.locationFilter}`}>
              <Button variant="ghost" className="text-gold hover:text-gold/80">
                View All <ChevronRight size={18} className="ml-1" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-testid="destination-yachts">
            {yachts.map((yacht) => (
              <Link
                key={yacht.yacht_id}
                to={`/yacht/${yacht.yacht_id}`}
                className="group glass-light overflow-hidden hover:bg-white/10 transition-colors"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={yacht.images?.[0]}
                    alt={yacht.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs uppercase tracking-[0.2em] text-gold mb-2">{yacht.yacht_type}</p>
                  <h3 className="text-xl font-medium mb-2 group-hover:text-gold transition-colors">
                    {yacht.name}
                  </h3>
                  <p className="text-gold font-semibold">
                    {yacht.charter_weekly_usd 
                      ? `$${yacht.charter_weekly_usd.toLocaleString()}/week`
                      : `$${yacht.price_usd?.toLocaleString()}`
                    }
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto" data-testid="faq-section">
        <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Playfair Display, serif' }}>
          Frequently Asked Questions
        </h2>
        <div className="max-w-3xl mx-auto space-y-4">
          {destination.faqs.map((faq, idx) => (
            <div key={idx} className="glass-light">
              <button
                onClick={() => setExpandedFaq(expandedFaq === idx ? -1 : idx)}
                className="w-full p-6 text-left flex justify-between items-center"
                data-testid={`faq-${idx}`}
              >
                <span className="font-medium text-lg pr-4">{faq.question}</span>
                <ChevronRight 
                  size={20} 
                  className={`text-gold shrink-0 transition-transform ${expandedFaq === idx ? 'rotate-90' : ''}`} 
                />
              </button>
              {expandedFaq === idx && (
                <div className="px-6 pb-6 text-white/70 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        <div className="glass-heavy p-12 text-center">
          <h2 className="text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Ready to Explore {destination.name}?
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Our team specializes in crafting bespoke yacht experiences across Southeast Asia. 
            Let us help you plan the perfect voyage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/yacht-finder">
              <Button className="bg-gold text-[#0A192F] hover:bg-[#E5C55D]">
                Find Your Yacht
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-gold text-gold hover:bg-gold hover:text-[#0A192F]">
                Contact an Expert
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
